import type { ActivityType, BadgeUniverso, ParticipantBadge, UserRole } from './types';

// =============================================================================
// API Endpoints
// =============================================================================

export const API_ENDPOINTS = {
  // Auth
  AUTH_LOGIN: '/api/auth/login',
  AUTH_REGISTER: '/api/auth/register',
  AUTH_REFRESH: '/api/auth/refresh',
  AUTH_LOGOUT: '/api/auth/logout',
  AUTH_ME: '/api/auth/me',

  // Users
  USERS: '/api/users',
  USER: (id: string) => `/api/users/${id}`,

  // Health
  HEALTH: '/api/health',
  READY: '/api/ready',
} as const;

// =============================================================================
// HTTP Status Codes
// =============================================================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
} as const;

// =============================================================================
// Auth Constants
// =============================================================================

export const AUTH = {
  ACCESS_TOKEN_KEY: 'accessToken',
  REFRESH_TOKEN_KEY: 'refreshToken',
  TOKEN_PREFIX: 'Bearer',
} as const;

// =============================================================================
// Pagination Defaults
// =============================================================================

export const PAGINATION = {
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  DEFAULT_OFFSET: 0,
} as const;

// =============================================================================
// Badge Constants
// =============================================================================

/**
 * The seven DIGGER universes, in display order. Brand tokens — never
 * translated. Mirrors models.ValidBadgeUniversos in the Go API and the
 * badges_universo_check constraint in migration 024.
 */
export const BADGE_UNIVERSOS = [
  'Code',
  'Bot',
  'Play',
  'Make',
  'Green',
  'Link',
  'Voice',
] as const;

/** Maximum number of labels a single badge may carry. */
export const BADGE_MAX_LABELS = 20;

/** Maximum length of an individual badge label. */
export const BADGE_MAX_LABEL_LENGTH = 40;

/**
 * Brand colour per universe, sampled from the DIGGER universe sheet.
 *
 * Black text throughout, matching the brand sheet. Contrast against each
 * background: Code 5.28:1, Bot 7.49:1, Play 2.97:1, Make 4.80:1, Green 6.37:1,
 * Voice 4.95:1, Link 12.68:1 — so every universe clears WCAG AA (4.5:1)
 * except Play, which is a deliberate brand-consistency choice. If Play ever
 * needs to pass AA, white text on #8E24AA gives 7.08:1.
 */
export const BADGE_UNIVERSO_COLORS: Record<BadgeUniverso, { bg: string; text: string }> = {
  Code: { bg: '#007BFF', text: '#000000' },
  Bot: { bg: '#FF6F00', text: '#000000' },
  Play: { bg: '#8E24AA', text: '#000000' },
  Make: { bg: '#607D8B', text: '#000000' },
  Green: { bg: '#43A047', text: '#000000' },
  Voice: { bg: '#E53935', text: '#000000' },
  Link: { bg: '#FBC02D', text: '#000000' },
};

/**
 * Universes that carry no downloadable certificate.
 *
 * `Link` is excluded by design, which is also why no Link universe tile ships
 * under the certificate assets — the other six each have one.
 */
export const BADGE_UNIVERSOS_WITHOUT_CERTIFICATE: readonly BadgeUniverso[] = ['Link'];

/**
 * Certificate artwork bundles the PWA ships, keyed the way the assets live
 * under `/certificate/<key>/` (a `top.png` header band and a `footer.png`
 * band each). Mirrors models.ValidCertificateTemplates in the Go API. A
 * centre with no key here has no certificate yet.
 */
export const CERTIFICATE_TEMPLATES = [
  { key: 'cicf', label: 'Centro de Inovação Carlos Fiolhais' },
  { key: 'ccdv', label: 'Centro de Cidadania Digital Valongo' },
] as const;

export type CertificateTemplateKey = (typeof CERTIFICATE_TEMPLATES)[number]['key'];

/** Reports whether `key` names shipped certificate artwork. */
export function isCertificateTemplate(
  key: string | null | undefined,
): key is CertificateTemplateKey {
  return key != null && CERTIFICATE_TEMPLATES.some((t) => t.key === key);
}

/**
 * Reports whether a badge assignment can be downloaded as a certificate.
 *
 * Revoked assignments never qualify, nor does the `Link` universe. A badge with
 * no labels does not either: the certificate's "competências" sentence is drawn
 * from them, and without it the document says almost nothing. Badge definitions
 * predating migration 024 carry no labels, so their certificates stay hidden
 * until someone classifies the definition — at which point every past
 * assignment of it becomes downloadable, since the fields are read live.
 *
 * An unclassified badge (no universo) still qualifies as long as it has
 * labels — its certificate simply renders without the centre tile.
 */
export function hasCertificate(
  badge: Pick<ParticipantBadge, 'badgeUniverso' | 'badgeLabels' | 'revokedAt'>,
): boolean {
  if (badge.revokedAt != null) return false;
  if (
    badge.badgeUniverso != null &&
    BADGE_UNIVERSOS_WITHOUT_CERTIFICATE.includes(badge.badgeUniverso)
  ) {
    return false;
  }
  // Go marshals an empty slice as null, so both cases mean "no labels".
  return (badge.badgeLabels?.length ?? 0) > 0;
}

// =============================================================================
// Activity Constants
// =============================================================================

/**
 * Activity types in display order. Mirrors models.ValidActivityTypes in the
 * Go API.
 */
export const ACTIVITY_TYPES = ['club', 'circuit', 'open_day'] as const;

/** pt-PT label per activity type. "Circuito" is the centres' own name for it. */
export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  club: 'Clube',
  circuit: 'Circuito',
  open_day: 'Dia Aberto',
};

/** Most sessions one repeat rule may create. Mirrors MaxSessionsPerRequest. */
export const ACTIVITY_MAX_SESSIONS_PER_REQUEST = 60;

/** Most days an open day may last. Mirrors MaxOpenDayDuration. */
export const ACTIVITY_OPEN_DAY_MAX_DAYS = 7;

/**
 * Reports whether an activity of this type is one event with one date, rather
 * than a run of sessions: an open day. It has one code for the whole of it,
 * and may last several days. Mirrors ActivityType.IsSingleEvent.
 */
export function isSingleEventActivity(type: ActivityType): boolean {
  return type === 'open_day';
}

// =============================================================================
// Staff Roles
// =============================================================================

/** Roles that belong to a member of staff. Mirrors models.IsStaffRole. */
export const STAFF_ROLES: readonly UserRole[] = ['super_admin', 'admin', 'coordinator'];

/** Reports whether `role` belongs to a member of staff. */
export function isStaffRole(role: string | null | undefined): boolean {
  return role != null && (STAFF_ROLES as readonly string[]).includes(role);
}

/** pt-PT label per role, as shown in the backoffice. */
export const USER_ROLE_LABELS: Record<UserRole, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  // Shown as what it now is, should an account from before the change remain.
  coordinator: 'Admin',
  user: 'Utilizador',
};
